/**
 * A commission plan's values (TMMRSL-0049) in three sections — Plan, Recurring commission and
 * Payment timing — each a Paper with its heading; a fourth, Bonuses and caps (TMMRSL-0051), only
 * when the plan was read with the TMMAPI-0105 extras (Advanced only: in Simple it says so). The
 * draft editor when `editable` (a DRAFT, OWNER or ADMIN); otherwise the same layout with every input
 * disabled. Simple is one rate for N months
 * or for life; Advanced is a phase table whose "from" months are derived, never typed. The rate and
 * cool-off fields show the adopted price card's limit live; a field shows its client error only once touched
 * (FRM-004/005), and the server's (a field the preview or a save names) at once, touched or not
 * (TMMRSL-0053) — shown as the limit that fixes it, never the server's text. The state lives in `CommissionPlanScreen`; the footer (Save,
 * Discard) is passed in.
 */
import { useId, type MutableRefObject, type ReactNode } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  CommissionPlanAfterEnd,
  CommissionPlanAppliesTo,
  CommissionPlanMode,
} from '@/api/commissionPlans';
import type { AffiliateMaximums } from '@/api/rateCards';
import { visuallyHidden } from '@/components/visuallyHidden';
import { intlLocale } from '@/i18n/constants';
import { useLocale } from '@/i18n/useLocale';
import { FIELD_WIDTH, Icon } from '@/theme';
import {
  addMilestone,
  addPhase,
  BOUNTY_PAYMENTS,
  canSwitchToSimple,
  MAX_MILESTONES,
  MAX_PHASES,
  NAME_MAX,
  phaseFroms,
  removeMilestone,
  removePhase,
  simpleBlockedKey,
  switchMode,
  type FormErrors,
  type FormField,
  type MilestoneForm,
  type PhaseForm,
  type PlanForm,
} from './planForm';
import {
  bountyLimitHint,
  coolOffLimitHint,
  milestoneLimitHint,
  rateLimitHint,
} from './planLimits';

export type FieldRefs = MutableRefObject<Partial<Record<FormField, HTMLElement | null>>>;

export interface CommissionPlanFormProps {
  editable: boolean;
  limits: AffiliateMaximums | null;
  /** Show "Bonuses and caps". The plan always carries the TMMAPI-0105 extras. */
  showExtras: boolean;
  /** The plan's currency — the extras' money inputs. */
  currency: string;
  form: PlanForm;
  onChange: (next: PlanForm) => void;
  /** Client errors (`formToBody`). */
  errors: FormErrors;
  /** Fields the server named (the preview's `fields`, or a 400's `fieldErrorsOf`). */
  serverErrors: Partial<Record<FormField, true>>;
  /**
   * Helper text for a server mark that isn't the field's limit. A duplicate plan name
   * (TMMRSL-0054) shows its own message; every other server mark stays the limit.
   */
  serverErrorText?: Partial<Record<FormField, string>>;
  touched: ReadonlySet<FormField>;
  onBlur: (field: FormField) => void;
  fieldRefs: FieldRefs;
  onSubmit: () => void;
  /** Save / Discard and the save state — rendered at the foot of an editable form. */
  footer?: ReactNode;
}

const APPLIES_TO: CommissionPlanAppliesTo[] = ['BASE', 'ALL'];
const AFTER_END: CommissionPlanAfterEnd[] = ['STOP', 'HONOR'];
const PAYMENTS = Array.from({ length: BOUNTY_PAYMENTS }, (_, i) => String(i + 1));

/** The plan currency as a money input's end adornment. */
function currencyAdornment(currency: string) {
  return currency ? <InputAdornment position="end">{currency}</InputAdornment> : undefined;
}

/** The asterisk a column heading carries when every input under it is required (FRM-003). */
function RequiredMark() {
  return (
    <Box component="span" aria-hidden="true">
      {' *'}
    </Box>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

export function CommissionPlanForm(props: CommissionPlanFormProps) {
  const { editable, limits, form, errors, serverErrors, touched, onBlur } = props;
  const { t } = useTranslation();
  const locale = useLocale();
  const phaseHintId = useId();
  const milestoneHintId = useId();
  const afterEndId = useId();
  const disabled = !editable;
  const rateHint = rateLimitHint(limits, locale, t);
  const coolOffHint = coolOffLimitHint(limits, t);
  const bountyHint = bountyLimitHint(limits, props.currency, locale, t);
  const milestoneHint = milestoneLimitHint(limits, props.currency, locale, t);

  function limitOf(field: FormField): string | null {
    if (field === 'simpleRate' || /^phases\.\d+\.rate$/.test(field)) return rateHint;
    if (field === 'coolOff') return coolOffHint;
    if (field === 'bounty') return bountyHint;
    if (/^milestones\.\d+\.amount$/.test(field)) return milestoneHint;
    return null;
  }

  /**
   * The error a field shows: the client's once touched, else the server's as its limit — touched
   * or not (TMMRSL-0053), so a field the preview names is marked as soon as it's named.
   */
  function errorOf(field: FormField): string | null {
    const client = errors[field];
    if (client && touched.has(field)) {
      return client.params ? t(client.key, client.params) : t(client.key);
    }
    if (serverErrors[field]) {
      return (
        props.serverErrorText?.[field] ??
        limitOf(field) ??
        t('commissionPlans.form.notAccepted')
      );
    }
    return null;
  }

  function helperOf(
    field: FormField,
    help: string | null,
  ): { error: boolean; helperText: string | undefined } {
    const error = errorOf(field);
    if (error) return { error: true, helperText: error };
    const parts = [help, limitOf(field)].filter((part): part is string => Boolean(part));
    return { error: false, helperText: parts.length > 0 ? parts.join(' ') : undefined };
  }

  const ref = (field: FormField) => (element: HTMLElement | null) => {
    props.fieldRefs.current[field] = element;
  };
  const set = (patch: Partial<PlanForm>) => props.onChange({ ...form, ...patch });

  function text(
    field: 'name' | 'simpleRate' | 'simpleMonths' | 'coolOff',
    label: string,
    kind: 'name' | 'percent' | 'whole',
    help: string | null = null,
    extra: { disabled?: boolean } = {},
  ) {
    // Required exactly where `formToBody` requires a value in the current mode (FRM-003): the
    // months only while "for N months" is chosen.
    const required = field !== 'simpleMonths' || !form.simpleLifetime;
    return (
      <TextField
        size="small"
        label={label}
        value={form[field]}
        onChange={(event) => set({ [field]: event.target.value })}
        onBlur={() => onBlur(field)}
        inputRef={ref(field)}
        required={required}
        disabled={disabled || extra.disabled}
        fullWidth
        sx={{ maxWidth: kind === 'name' ? FIELD_WIDTH.lg : FIELD_WIDTH.sm }}
        {...helperOf(field, help)}
        slotProps={{
          htmlInput:
            kind === 'name'
              ? { maxLength: NAME_MAX }
              : { inputMode: kind === 'whole' ? 'numeric' : 'decimal' },
        }}
      />
    );
  }

  /** A fieldset-level mark (mode, applies to, after the end) — only ever the server's. */
  function groupError(field: 'mode' | 'appliesTo' | 'afterEnd') {
    const error = errorOf(field);
    return error ? <FormHelperText error>{error}</FormHelperText> : null;
  }

  const simpleAllowed = canSwitchToSimple(form);
  const simpleButton = (
    <ToggleButton
      value="SIMPLE"
      disabled={disabled || (form.mode !== 'SIMPLE' && !simpleAllowed)}
      ref={form.mode === 'SIMPLE' ? ref('mode') : undefined}
    >
      {t('commissionPlans.mode.SIMPLE')}
    </ToggleButton>
  );

  const plan = (
    <Section title={t('commissionPlans.section.plan')}>
      <Stack spacing={2.5}>
        {text('name', t('commissionPlans.field.name'), 'name')}
        <Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.mode}
            aria-label={t('commissionPlans.mode.label')}
            onChange={(_, next: CommissionPlanMode | null) => {
              if (next !== null) props.onChange(switchMode(form, next));
            }}
            onBlur={() => onBlur('mode')}
          >
            {form.mode !== 'SIMPLE' && !simpleAllowed && !disabled ? (
              <Tooltip title={t(simpleBlockedKey(form))}>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  {simpleButton}
                </Box>
              </Tooltip>
            ) : (
              simpleButton
            )}
            <ToggleButton
              value="ADVANCED"
              disabled={disabled}
              ref={form.mode === 'ADVANCED' ? ref('mode') : undefined}
            >
              {t('commissionPlans.mode.ADVANCED')}
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {t('commissionPlans.mode.help')}
          </Typography>
          {groupError('mode')}
        </Box>
        <FormControl component="fieldset" disabled={disabled} error={errorOf('appliesTo') !== null}>
          <FormLabel component="legend">{t('commissionPlans.appliesTo.label')}</FormLabel>
          <RadioGroup
            value={form.appliesTo}
            onChange={(event) => set({ appliesTo: event.target.value as CommissionPlanAppliesTo })}
            onBlur={() => onBlur('appliesTo')}
          >
            {APPLIES_TO.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={
                  <Radio
                    slotProps={{
                      input: form.appliesTo === value ? { ref: ref('appliesTo') } : {},
                    }}
                  />
                }
                label={t(`commissionPlans.appliesTo.${value}_long`)}
              />
            ))}
          </RadioGroup>
          {groupError('appliesTo')}
        </FormControl>
      </Stack>
    </Section>
  );

  const simple = (
    <Stack spacing={2}>
      {text('simpleRate', t('commissionPlans.field.rate'), 'percent')}
      <FormControl component="fieldset" disabled={disabled}>
        <FormLabel component="legend">{t('commissionPlans.duration.label')}</FormLabel>
        <RadioGroup
          value={form.simpleLifetime ? 'life' : 'months'}
          onChange={(event) => set({ simpleLifetime: event.target.value === 'life' })}
        >
          <FormControlLabel
            value="months"
            control={<Radio />}
            label={t('commissionPlans.duration.months')}
          />
          <FormControlLabel
            value="life"
            control={<Radio />}
            label={t('commissionPlans.duration.life')}
          />
        </RadioGroup>
      </FormControl>
      {text('simpleMonths', t('commissionPlans.field.months'), 'whole', null, {
        disabled: form.simpleLifetime,
      })}
    </Stack>
  );

  const recurring = (
    <Section title={t('commissionPlans.section.recurring')}>
      {form.mode === 'SIMPLE' ? (
        simple
      ) : (
        <PhaseTable
          phases={form.phases}
          disabled={disabled}
          hint={rateHint}
          hintId={phaseHintId}
          errorOf={errorOf}
          onChange={(phases) => set({ phases })}
          onBlur={onBlur}
          refFor={ref}
        />
      )}
      <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 1.5 }}>
        {t('commissionPlans.recurring.month1')}
      </Typography>
    </Section>
  );

  const timing = (
    <Section title={t('commissionPlans.section.timing')}>
      <Stack spacing={2.5}>
        {text('coolOff', t('commissionPlans.field.coolOff'), 'whole', t('commissionPlans.coolOff.help'))}
        <FormControl component="fieldset" disabled={disabled} error={errorOf('afterEnd') !== null}>
          <FormLabel component="legend">{t('commissionPlans.afterEnd.label')}</FormLabel>
          <RadioGroup
            value={form.afterEnd}
            onChange={(event) => set({ afterEnd: event.target.value as CommissionPlanAfterEnd })}
            onBlur={() => onBlur('afterEnd')}
          >
            {AFTER_END.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                sx={{ alignItems: 'flex-start', mb: 1 }}
                control={
                  <Radio
                    sx={{ pt: 0.5 }}
                    slotProps={{
                      input: {
                        'aria-describedby': `${afterEndId}-${value}`,
                        ...(form.afterEnd === value ? { ref: ref('afterEnd') } : {}),
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ pt: 0.75 }}>
                    <Typography variant="body2">{t(`commissionPlans.afterEnd.${value}`)}</Typography>
                    <Typography
                      id={`${afterEndId}-${value}`}
                      variant="caption"
                      component="p"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t(`commissionPlans.afterEnd.${value}_help`)}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
          {groupError('afterEnd')}
        </FormControl>
      </Stack>
    </Section>
  );

  /** A money input of the extras, in the plan currency. */
  function money(field: 'bounty' | 'referralCap' | 'monthCap', label: string, help: string) {
    return (
      <TextField
        size="small"
        label={label}
        value={form[field]}
        onChange={(event) => set({ [field]: event.target.value })}
        onBlur={() => onBlur(field)}
        inputRef={ref(field)}
        required
        disabled={disabled}
        fullWidth
        sx={{ maxWidth: FIELD_WIDTH.sm }}
        {...helperOf(field, help)}
        slotProps={{
          input: { endAdornment: currencyAdornment(props.currency) },
          htmlInput: { inputMode: 'decimal' },
        }}
      />
    );
  }

  /** A switch that shows its fields while on. */
  function toggle(
    on: boolean,
    key: 'bountyOn' | 'referralCapOn' | 'monthCapOn',
    label: string,
    fields: ReactNode,
  ) {
    return (
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={on}
              disabled={disabled}
              onChange={(_, checked) => set({ [key]: checked })}
            />
          }
          label={label}
        />
        {on ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {fields}
          </Stack>
        ) : null}
      </Box>
    );
  }

  const bountyPaymentError = errorOf('bountyOnPayment');
  const extras = props.showExtras ? (
    <Section title={t('commissionPlans.section.extras')}>
      {form.mode === 'SIMPLE' ? (
        <Typography variant="body2">{t('commissionPlans.extras.advancedOnly')}</Typography>
      ) : (
        <Stack spacing={2.5}>
          {toggle(
            form.bountyOn,
            'bountyOn',
            t('commissionPlans.extras.bounty'),
            <>
              {money(
                'bounty',
                t('commissionPlans.extras.bountyAmount'),
                t('commissionPlans.extras.bountyHelp'),
              )}
              <TextField
                select
                size="small"
                label={t('commissionPlans.extras.bountyOn')}
                value={form.bountyOnPayment}
                onChange={(event) => set({ bountyOnPayment: event.target.value })}
                onBlur={() => onBlur('bountyOnPayment')}
                inputRef={ref('bountyOnPayment')}
                required
                disabled={disabled}
                error={bountyPaymentError !== null}
                helperText={bountyPaymentError ?? undefined}
                sx={{ maxWidth: FIELD_WIDTH.sm }}
              >
                {PAYMENTS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {new Intl.NumberFormat(intlLocale(locale)).format(Number(n))}
                  </MenuItem>
                ))}
              </TextField>
            </>,
          )}
          <Box>
            <Typography variant="body2" component="h3" sx={{ fontWeight: 500, mb: 1 }}>
              {t('commissionPlans.extras.milestones')}
            </Typography>
            <MilestoneTable
              milestones={form.milestones}
              currency={props.currency}
              disabled={disabled}
              hint={milestoneHint}
              hintId={milestoneHintId}
              errorOf={errorOf}
              onChange={(milestones) => set({ milestones })}
              onBlur={onBlur}
              refFor={ref}
            />
          </Box>
          {toggle(
            form.referralCapOn,
            'referralCapOn',
            t('commissionPlans.extras.referralCap'),
            money(
              'referralCap',
              t('commissionPlans.extras.referralCap'),
              t('commissionPlans.extras.referralCapHelp'),
            ),
          )}
          {toggle(
            form.monthCapOn,
            'monthCapOn',
            t('commissionPlans.extras.monthCap'),
            money(
              'monthCap',
              t('commissionPlans.extras.monthCap'),
              t('commissionPlans.extras.monthCapHelp'),
            ),
          )}
        </Stack>
      )}
    </Section>
  ) : null;

  const sections = (
    <Stack spacing={2.5}>
      {plan}
      {recurring}
      {timing}
      {extras}
    </Stack>
  );

  if (!editable) return sections;

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      {sections}
      {props.footer}
    </Box>
  );
}

interface PhaseTableProps {
  phases: PhaseForm[];
  disabled: boolean;
  hint: string | null;
  hintId: string;
  errorOf: (field: FormField) => string | null;
  onChange: (phases: PhaseForm[]) => void;
  onBlur: (field: FormField) => void;
  refFor: (field: FormField) => (element: HTMLElement | null) => void;
}

/**
 * The Advanced phases (A11Y-006): each row's "from" is derived and read-only; each input carries
 * an `aria-label` naming its phase, and the rate cells are described by the price card's limit.
 */
function PhaseTable({
  phases,
  disabled,
  hint,
  hintId,
  errorOf,
  onChange,
  onBlur,
  refFor,
}: PhaseTableProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const froms = phaseFroms(phases);
  const count = (value: number | null) =>
    value === null ? t('statements.unavailable') : new Intl.NumberFormat(intlLocale(locale)).format(value);

  function update(index: number, column: keyof PhaseForm, value: string) {
    onChange(phases.map((phase, i) => (i === index ? { ...phase, [column]: value } : phase)));
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={t('commissionPlans.phases.label')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('commissionPlans.phases.from')}</TableCell>
              <TableCell>{t('commissionPlans.phases.to')}</TableCell>
              <TableCell>
                {t('commissionPlans.phases.rate')}
                <RequiredMark />
              </TableCell>
              <TableCell>
                <Box component="span" sx={visuallyHidden}>
                  {t('commissionPlans.col.actions')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {phases.map((phase, index) => {
              const n = index + 1;
              const last = index === phases.length - 1;
              const toField: FormField = `phases.${index}.to`;
              const rateField: FormField = `phases.${index}.rate`;
              const toError = errorOf(toField);
              const rateError = errorOf(rateField);
              return (
                // Phases have no identity of their own; a row is its position.
                <TableRow key={index}>
                  <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                    {count(froms[index] ?? null)}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={phase.to}
                      // Every row but the last ends somewhere; the last may run onward.
                      required={!last}
                      disabled={disabled}
                      placeholder={last ? t('commissionPlans.phases.onward') : undefined}
                      onChange={(event) => update(index, 'to', event.target.value)}
                      onBlur={() => onBlur(toField)}
                      inputRef={refFor(toField)}
                      error={toError !== null}
                      helperText={toError ?? undefined}
                      sx={{ maxWidth: FIELD_WIDTH.xs }}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t('commissionPlans.phases.toAria', { n }),
                          inputMode: 'numeric',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={phase.rate}
                      required
                      disabled={disabled}
                      onChange={(event) => update(index, 'rate', event.target.value)}
                      onBlur={() => onBlur(rateField)}
                      inputRef={refFor(rateField)}
                      error={rateError !== null}
                      helperText={rateError ?? undefined}
                      sx={{ maxWidth: FIELD_WIDTH.xs }}
                      slotProps={{
                        htmlInput: {
                          'aria-label': t('commissionPlans.phases.rateAria', { n }),
                          ...(!rateError && hint ? { 'aria-describedby': hintId } : {}),
                          inputMode: 'decimal',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {phases.length > 1 ? (
                      <Tooltip title={t('commissionPlans.phases.remove', { n })}>
                        <span>
                          <IconButton
                            aria-label={t('commissionPlans.phases.remove', { n })}
                            disabled={disabled}
                            onClick={() => onChange(removePhase(phases, index))}
                          >
                            <Icon name="delete" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <Button
        startIcon={<Icon name="add" />}
        onClick={() => onChange(addPhase(phases))}
        disabled={disabled || phases.length >= MAX_PHASES}
        sx={{ mt: 1 }}
      >
        {t('commissionPlans.phases.add')}
      </Button>
      {hint ? <FormHelperText id={hintId}>{hint}</FormHelperText> : null}
    </Box>
  );
}

interface MilestoneTableProps {
  milestones: MilestoneForm[];
  currency: string;
  disabled: boolean;
  hint: string | null;
  hintId: string;
  errorOf: (field: FormField) => string | null;
  onChange: (milestones: MilestoneForm[]) => void;
  onBlur: (field: FormField) => void;
  refFor: (field: FormField) => (element: HTMLElement | null) => void;
}

/**
 * The milestone bonuses (A11Y-006): each input carries an `aria-label` naming its bonus, and the
 * amounts are described by the price card's limit. At most five rows.
 */
function MilestoneTable({
  milestones,
  currency,
  disabled,
  hint,
  hintId,
  errorOf,
  onChange,
  onBlur,
  refFor,
}: MilestoneTableProps) {
  const { t } = useTranslation();

  function update(index: number, column: keyof MilestoneForm, value: string) {
    onChange(milestones.map((row, i) => (i === index ? { ...row, [column]: value } : row)));
  }

  return (
    <Box>
      {milestones.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={t('commissionPlans.extras.milestones')}>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('commissionPlans.extras.at')}
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  {t('commissionPlans.extras.amount')}
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <Box component="span" sx={visuallyHidden}>
                    {t('commissionPlans.col.actions')}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {milestones.map((row, index) => {
                const n = index + 1;
                const atField: FormField = `milestones.${index}.at`;
                const amountField: FormField = `milestones.${index}.amount`;
                const atError = errorOf(atField);
                const amountError = errorOf(amountField);
                return (
                  // Milestone rows have no identity of their own; a row is its position.
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.at}
                        required
                        disabled={disabled}
                        onChange={(event) => update(index, 'at', event.target.value)}
                        onBlur={() => onBlur(atField)}
                        inputRef={refFor(atField)}
                        error={atError !== null}
                        helperText={atError ?? undefined}
                        sx={{ maxWidth: FIELD_WIDTH.xs }}
                        slotProps={{
                          htmlInput: {
                            'aria-label': t('commissionPlans.extras.atAria', { n }),
                            inputMode: 'numeric',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.amount}
                        required
                        disabled={disabled}
                        onChange={(event) => update(index, 'amount', event.target.value)}
                        onBlur={() => onBlur(amountField)}
                        inputRef={refFor(amountField)}
                        error={amountError !== null}
                        helperText={amountError ?? undefined}
                        sx={{ maxWidth: FIELD_WIDTH.sm }}
                        slotProps={{
                          input: { endAdornment: currencyAdornment(currency) },
                          htmlInput: {
                            'aria-label': t('commissionPlans.extras.amountAria', { n }),
                            ...(!amountError && hint ? { 'aria-describedby': hintId } : {}),
                            inputMode: 'decimal',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('commissionPlans.extras.remove', { n })}>
                        <span>
                          <IconButton
                            aria-label={t('commissionPlans.extras.remove', { n })}
                            disabled={disabled}
                            onClick={() => onChange(removeMilestone(milestones, index))}
                          >
                            <Icon name="delete" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      <Button
        startIcon={<Icon name="add" />}
        onClick={() => onChange(addMilestone(milestones))}
        disabled={disabled || milestones.length >= MAX_MILESTONES}
        sx={{ mt: 1 }}
      >
        {t('commissionPlans.extras.add')}
      </Button>
      <FormHelperText>{t('commissionPlans.extras.milestonesHelp')}</FormHelperText>
      {hint ? <FormHelperText id={hintId}>{hint}</FormHelperText> : null}
    </Box>
  );
}
